/**
 * SessionViewer - Read-only session transcript viewer
 *
 * Platform-agnostic component for viewing session transcripts.
 * Used by the web viewer app. For interactive chat, Electron uses ChatDisplay.
 *
 * Renders a session's messages as turn cards with gradient fade at top/bottom.
 */

import type { ReactNode } from 'react'
import { useMemo, useState, useCallback } from 'react'
import type { StoredSession } from '@craft-agent/core'
import { cn } from '../../lib/utils'
import { CHAT_LAYOUT, CHAT_CLASSES } from '../../lib/layout'
import { PlatformProvider, type PlatformActions } from '../../context'
import { TurnCard } from './TurnCard'
import { UserMessageBubble } from './UserMessageBubble'
import { SystemMessage } from './SystemMessage'
import {
  groupMessagesByTurn,
  storedToMessage,
  getAssistantTurnUiKey,
  type ActivityItem,
} from './turn-utils'

export type SessionViewerMode = 'interactive' | 'readonly'

export interface SessionViewerProps {
  /** Session data to display */
  session: StoredSession
  /** View mode - 'readonly' for web viewer, 'interactive' for Electron */
  mode?: SessionViewerMode
  /** Platform-specific actions (file opening, URL handling, etc.) */
  platformActions?: PlatformActions
  /** Additional className for the container */
  className?: string
  /** Callback when a turn is clicked */
  onTurnClick?: (turnId: string) => void
  /** Callback when an activity is clicked */
  onActivityClick?: (activity: ActivityItem) => void
  /** Default expanded state for turns (true for readonly, false for interactive) */
  defaultExpanded?: boolean
  /** Custom header content */
  header?: ReactNode
  /** Custom footer content (input area for interactive mode) */
  footer?: ReactNode
  /** Optional session folder path for stripping from file paths in tool display */
  sessionFolderPath?: string
}

/**
 * MoziLogo - The Mozi "M" logo for branding
 */
function CraftAgentLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1024 1024"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(93, 208)">
        <path
          d="M343.484199,5.73289404 C357.102917,15.4979078 366.953036,27.6758354 367.955839,44.9386955 C367.955839,45.6026517 368.489589,207.073551 368.473415,208.773926 C368.586635,217.615879 368.942468,222.911334 375.832699,229.210821 C382.91702,233.826126 388.998538,233.24314 396.859224,230.830226 C407.011181,224.138082 601.673751,26.5098636 603.42057,24.7285178 C617.96122,9.86237747 630.544787,-0.728532963 652.121236,0.194528038 C669.07185,2.44550136 680.620265,10.4939455 689.483754,24.9066524 C694.643339,35.254652 694.918302,44.3880977 694.885953,55.7239346 C694.885953,56.5984134 695.225612,205.195041 695.209438,206.879222 C695.322658,216.449907 696.212242,221.907303 702.552548,229.210821 C709.361907,233.92329 715.912478,233.032617 723.579073,230.830226 C726.684529,228.676417 726.684529,228.676417 729.676765,225.939622 C730.776614,224.919397 746.967038,208.919673 753.727874,201.761901 C767.006934,188.142703 780.415387,181.891799 799.598047,183.867473 C817.3412,188.660913 826.867833,198.620255 835.181397,214.636173 C837.025262,220.190733 837,224.433575 837,230.279628 C837,231.380824 836.798822,390.341645 836.798822,449.449937 C836.798822,508.558229 739.753323,585.998189 739.753323,587.099384 C723.95108,601.398733 711.610128,609.997775 689.66167,607.601055 C675.412156,604.475603 663.718174,594.856335 657.086731,581.998258 C652.541767,571.407347 651.490441,562.354872 651.587486,550.905677 C651.571312,550.015004 651.571312,550.015004 651.538963,545.561639 C651.522789,539.796556 651.231653,399.296956 651.199304,395.475159 C651.199304,394.163441 651.199304,392.851723 651.199304,391.491422 C650.53616,385.677757 648.659947,382.374171 644.228202,378.600956 C636.917441,375.491698 625.72486,374.147592 621.551904,377.872224 C617.637735,381.434916 639.084791,360.382647 577.816732,422.324899 C516.548673,484.26715 431.633861,571.715034 419.503174,582.241168 C407.372486,592.767303 398.492823,602.257018 385.537249,606.532247 C366.483982,608.475534 351.118445,606.904711 337.20859,592.459616 C327.2776,579.633926 324.738243,566.840624 324.867637,550.905677 C324.851463,549.140525 324.479455,392.851723 324.479455,391.491422 C323.816311,385.677757 323.541349,379.443047 316.796686,376.5767 C310.052024,373.710352 306.283424,373.337889 298.196299,376.5767 C290.109174,379.81551 280.016442,391.086571 272.010188,399.248373 C270.910339,400.365763 91.9746122,580.621763 82.7876382,589.236999 C68.1404582,602.972633 59.0923621,608.184041 39.2789059,607.244786 C24.1559822,605.54441 13.917682,597.933205 5.44237498,585.479979 C1.12385025,578.063103 0,572.751454 0,564.200994 C0,563.083604 0.315137748,420.219672 0.347486248,403.75032 C0.525402997,283.525673 30.7550761,271.655433 111.6425,190.474647 C116.025722,186.086058 282.474928,18.8338826 283.590951,17.6841049 C299.959292,1.55482846 322.118015,-6.07257034 343.484199,5.73289404 Z"
          fill="currentColor"
          fillRule="nonzero"
        />
      </g>
    </svg>
  )
}

/**
 * SessionViewer - Read-only session transcript viewer component
 */
export function SessionViewer({
  session,
  mode = 'readonly',
  platformActions = {},
  className,
  onTurnClick,
  onActivityClick,
  defaultExpanded = false,
  header,
  footer,
  sessionFolderPath,
}: SessionViewerProps) {
  // Convert StoredMessage[] to Message[] and group into turns
  const turns = useMemo(
    () => groupMessagesByTurn(session.messages.map(storedToMessage)),
    [session.messages]
  )

  // Track expanded turns (for controlled state)
  const [expandedTurns, setExpandedTurns] = useState<Set<string>>(() => {
    // Default: all turns collapsed, can override with defaultExpanded prop
    if (defaultExpanded) {
      return new Set(
        turns
          .map((turn, index) => turn.type === 'assistant' ? getAssistantTurnUiKey(turn, index) : null)
          .filter((key): key is string => !!key)
      )
    }
    return new Set()
  })

  // Track expanded activity groups
  const [expandedActivityGroups, setExpandedActivityGroups] = useState<Set<string>>(new Set())

  const handleExpandedChange = useCallback((turnId: string, expanded: boolean) => {
    setExpandedTurns(prev => {
      const next = new Set(prev)
      if (expanded) {
        next.add(turnId)
      } else {
        next.delete(turnId)
      }
      return next
    })
  }, [])

  const handleExpandedActivityGroupsChange = useCallback((groups: Set<string>) => {
    setExpandedActivityGroups(groups)
  }, [])

  const handleOpenActivityDetails = useCallback((activity: ActivityItem) => {
    if (onActivityClick) {
      onActivityClick(activity)
    } else if (platformActions.onOpenActivityDetails) {
      platformActions.onOpenActivityDetails(session.id, activity.id)
    }
  }, [onActivityClick, platformActions, session.id])

  const handleOpenTurnDetails = useCallback((turnId: string) => {
    if (onTurnClick) {
      onTurnClick(turnId)
    } else if (platformActions.onOpenTurnDetails) {
      platformActions.onOpenTurnDetails(session.id, turnId)
    }
  }, [onTurnClick, platformActions, session.id])

  return (
    <PlatformProvider actions={platformActions}>
      <div className={cn("flex flex-col h-full", className)}>
        {/* Header */}
        {header && (
          <div className="shrink-0 border-b">
            {header}
          </div>
        )}

        {/* Messages area with gradient fade mask at top/bottom */}
        <div
          className="flex-1 min-h-0"
          style={{
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 32px, black calc(100% - 32px), transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 32px, black calc(100% - 32px), transparent 100%)'
          }}
        >
          <div className="h-full overflow-y-auto">
            <div className={cn(CHAT_LAYOUT.maxWidth, "mx-auto", CHAT_LAYOUT.containerPadding, CHAT_LAYOUT.messageSpacing)}>
            {turns.map((turn, index) => {
              if (turn.type === 'user') {
                return (
                  <div key={turn.message.id} className={CHAT_LAYOUT.userMessagePadding}>
                    <UserMessageBubble
                      content={turn.message.content}
                      attachments={turn.message.attachments}
                      badges={turn.message.badges}
                      onUrlClick={platformActions.onOpenUrl}
                      onFileClick={platformActions.onOpenFile}
                    />
                  </div>
                )
              }

              if (turn.type === 'system') {
                const msgType = turn.message.role === 'error' ? 'error' :
                               turn.message.role === 'warning' ? 'warning' :
                               turn.message.role === 'info' ? 'info' : 'system'
                return (
                  <SystemMessage
                    key={turn.message.id}
                    content={turn.message.content}
                    type={msgType}
                  />
                )
              }

              if (turn.type === 'assistant') {
                const assistantUiKey = getAssistantTurnUiKey(turn, index)
                return (
                  <TurnCard
                    key={assistantUiKey}
                    turnId={turn.turnId}
                    activities={turn.activities}
                    response={turn.response}
                    intent={turn.intent}
                    isStreaming={turn.isStreaming}
                    isComplete={turn.isComplete}
                    isExpanded={expandedTurns.has(assistantUiKey)}
                    onExpandedChange={(expanded) => handleExpandedChange(assistantUiKey, expanded)}
                    onOpenFile={platformActions.onOpenFile}
                    onOpenUrl={platformActions.onOpenUrl}
                    onPopOut={platformActions.onOpenMarkdownPreview}
                    onOpenDetails={() => handleOpenTurnDetails(turn.turnId)}
                    onOpenActivityDetails={handleOpenActivityDetails}
                    todos={turn.todos}
                    expandedActivityGroups={expandedActivityGroups}
                    onExpandedActivityGroupsChange={handleExpandedActivityGroupsChange}
                    hasEditOrWriteActivities={turn.activities.some(a =>
                      a.toolName === 'Edit' || a.toolName === 'Write'
                    )}
                    onOpenMultiFileDiff={platformActions.onOpenMultiFileDiff
                      ? () => platformActions.onOpenMultiFileDiff!(session.id, turn.turnId)
                      : undefined
                    }
                    sessionFolderPath={sessionFolderPath}
                    annotationInteractionMode={mode === 'readonly' ? 'tooltip-only' : 'interactive'}
                  />
                )
              }

              return null
            })}

            {/* Bottom branding */}
            <div className={CHAT_CLASSES.brandingContainer}>
              <CraftAgentLogo className="w-8 h-8 text-[#9570BE]/40" />
            </div>
            </div>
          </div>
        </div>

        {/* Footer (input area) */}
        {footer && (
          <div className="shrink-0 border-t">
            {footer}
          </div>
        )}
      </div>
    </PlatformProvider>
  )
}
